import { analyzeWall } from "../analyze";
import { parametricToPolygon } from "../geometry/parametric";
import { buildSection } from "../geometry/section";
import { propertyLineExcess } from "../geometry/propertyLine";
import { deg2rad } from "../math/angles";
import { fmt } from "../format/number";
import { centroid } from "../math/polygon";
import { computeQuantities, costEstimate, makeDefaultPricing } from "../quantities";
import { DEFAULT_STONE_PRESET } from "../defaults";
import { stoneMaterial } from "../materials";
import type { Pricing, PropertyLine, StructureType, WallInput } from "../types/input";
import type { JointCaseResult, WallResult } from "../types/result";

/**
 * Otomatik ön boyutlandırma ve maliyet karşılaştırması.
 *
 * Kullanıcı yalnız duvar yüksekliğini (ve varsa sürşarjı) verir; zemin, dolgu, su, deprem, malzeme ve kontrol ayarları
 * seçili duvardan alınır. Her duvar tipi için biçim aileleri taranır — taş: paralel yüzlü dolguya yatık duvar (KGM 5:1),
 * ön yüzü eğik arka yüzü düşey duvar, kademeli gövde; hepsi temel bloklu/bloksuz · betonarme: gövde/taban kalınlıkları,
 * burun oranı, kayma dişi. Her biçimde **tüm kontrolleri sağlayan en küçük genişlik** adım adım aranır (genişlik arttıkça
 * güvenlik artar — monoton kabul), sonra 1 m duvar için keşif tutarı hesaplanır. En düşük maliyetli aday "kurtaran boyut"
 * olarak önerilir; ondan en az 5 cm dar tabanlı sağlayan bir biçim varsa en dar tabanlısı da yanında verilir (`narrowest`).
 *
 * Sonuç tavsiye niteliğindedir: seçilen boyut projeye uygulanır ve normal hesap/rapor akışında yeniden doğrulanır.
 */
export interface AutoSizeOptions {
  /** Toplam duvar yüksekliği H (m) — taban (y = 0) ile tepe arası */
  height: number;
  /** Karşılaştırılacak tipler (varsayılan: taş ağırlık + betonarme konsol) */
  types?: StructureType[];
  /** Birim fiyat tablosu; verilmezse varsayılan (yaklaşık) fiyatlar */
  pricing?: Pricing;
  /** Boyut adımı (m) — varsayılan 5 cm */
  step?: number;
  /** Taban genişliği üst sınırı (H çarpanı) — varsayılan 2,0·H */
  maxBaseRatio?: number;
  /** Kaba tarama adımı (m) — varsayılan 0,25; ilk sağlayan genişlik bulununca `step` ile incelenir */
  coarseStep?: number;
  /** Düzgün yayılı sürşarj (kN/m²) — verilmezse duvarın kendi değeri */
  surcharge?: number;
  /** Pasif direnç hesaba katılsın mı — verilmezse duvarın kendi ayarı */
  passive?: boolean;
  /** İlerleme bildirimi (0–1) — Web Worker'da arayüze çubuk basmak için */
  onProgress?: (done: number, total: number) => void;
}

/** Boyut satırı: değer + birim (verilmezse "m"; eğimlerde "" = yatay/düşey oranı) + isteğe bağlı açıklama (ör. "düşey") */
export interface AutoSizeDim { label: string; value: number; unit?: string; note?: string }

/** Taş duvar biçim aileleri: paralel yüzlü yatık (KGM 5:1) · ön eğik/arka düşey · kademeli gövde (üst/alt kalınlık) */
export type MasonryFamily = "lean" | "back-vertical" | "stepped";

export interface AutoSizeCandidate {
  structureType: StructureType;
  label: string;
  /** Kesit biçimi (kısa Türkçe tanım) — ör. "Paralel yüzlü, dolguya yatık duvar" */
  shape: string;
  wall: WallInput;                 // önerilen duvar (uygulanmaya hazır)
  result: WallResult;
  /** Boyut özeti — arayüzde tablo satırları */
  dims: AutoSizeDim[];
  B: number;
  /** 1 m duvar için keşif: metraj satırları ve tutar (fiyat yoksa null) */
  cost: number | null;
  currency: string;
  materials: { concrete: number; masonry: number; steel: number; formwork: number; excavation: number };   // m³ / m³ / kg / m² / m³ (1 m için)
  /** En zorlayan kontroller: oran = etki / dayanım (1'e yakın = sınırda); yönetmelik ve klasik GK oranlarının büyüğü */
  governing: { label: string; ratio: number }[];
  tried: number;
  /**
   * Aynı tipte kontrolleri sağlayan **en dar tabanlı** aday (en küçük B; eşitlikte en ucuz) — yalnız bu (en ucuz) adaydan
   * B'si en az 5 cm küçükse dolu (Cem 2026-10-06: rakip programlarla taban genişliği kıyaslanıyor; en ucuz çoğu kez en dar değil).
   * İç içe adayda bu alan boştur.
   */
  narrowest?: AutoSizeCandidate;
}

export interface AutoSizeReport {
  height: number;
  candidates: AutoSizeCandidate[];
  /** Hiçbir boyutun sağlamadığı tipler için açıklama */
  failures: {
    structureType: StructureType; label: string; reason: string;
    /** Parsel sınırı yüzünden sağlamadıysa: sınır olmasaydı en ucuz kesit (betonarme) — sınıra uzaklık topuk/burun kadar olursa çözüm çıkar */
    withoutLine?: { B: number; toe: number; heel: number; cost: number | null; currency: string };
  }[];
  tried: number;
  ms: number;
  priced: boolean;
}

/**
 * Hiçbir aday geçmediyse neden. Hesap hatası yalnız hiçbir aday hesaplanamadıysa bildirilir — taramanın uç adaylarından birinin
 * hatası (ör. çok geniş tabanda Coulomb kaması yok) gerçek nedeni (kontroller sağlanmıyor) gizlememeli. Kontroller
 * sağlanmıyorsa en yakın adayın zorlayan kontrolü ve zemin çok zayıfsa uyarı eklenir.
 */
function reasonOf(c: Counter, maxB: number, base: WallInput): string {
  if (c.analyzed === 0 && c.error) return `Hesap yapılamadı: ${c.error}`;
  const near = c.closest ? ` En yakın aday (B = ${fmt(c.closest.B)} m): ${c.closest.label} oranı ${fmt(c.closest.ratio)} > 1.` : "";
  const bf = base.backfill.layers[0], fs = base.foundationSoil;
  const weak = [bf && bf.phi < WEAK_PHI && bf.c < 10 ? `dolgu φ = ${fmt(bf.phi, 0)}°` : "", fs.drained && fs.phi < WEAK_PHI && fs.c < 10 ? `temel zemini φ = ${fmt(fs.phi, 0)}°` : ""].filter(Boolean);
  // Drenajlı kaymada c katılmaz (TBDY Denk. 16.10): küçük φ'li kohezyonlu temel zemini büyük olasılıkla drenajsız deney (UU) değeridir
  const undrained = fs.drained && fs.phi < WEAK_PHI && fs.c >= 10
    ? ` Temel zemini φ = ${fmt(fs.phi, 0)}° drenajlı alındı: drenajlı kaymada kohezyon katılmaz (TBDY Denk. 16.10). Değer drenajsız deneyden (UU) geldiyse temel zeminini "Drenajsız (cu)" seçin.`
    : "";
  const hint = (weak.length ? ` Zemin çok zayıf görünüyor (${weak.join(", ")}) — rapor değerlerini kontrol edin.` : "") + undrained;
  // Arka su (Cem 2026-10-01: "8 m'ye kadar denedim — imkânsız"): dolgu su altındaysa su itkisi + kaldırma kaymayı belirler
  const hw = base.water.backLevel;
  const water = hw !== null && hw > 0
    ? ` Duvar arkasında yeraltı suyu var (hw = ${fmt(hw)} m): su itkisi ve taban kaldırması tabanı büyütüyor.${base.quantities.drainage.enabled ? " Barbakan/dren yapılacaksa su itkisi alınmaz (TBDY 16.12.3.1) — Yeraltı suyu'nu kapatın." : ""}`
    : "";
  const pl = base.propertyLine;
  // Arka sınır: topuk kısa kalınca üstündeki dolgu ağırlığı kaymaya katkı vermez — depremde konsol duvar çoğu kez sağlamaz
  const line = !pl ? "" : ` Parsel sınırı ${pl.side === "front" ? "ön (burun)" : "arka (topuk)"} tarafında, gövde yüzünden ${fmt(pl.clearance)} m: o yönde temel çıkıntısı sınırlı.`
    + (pl.side === "back" && c.closest?.label.startsWith("Kayma") ? " Topuk kısa olduğundan üstündeki dolgu ağırlığı kaymaya katkı vermiyor: pasif direnç + kayma dişi, ağırlık (taş) duvar ya da ankrajlı/kazıklı çözüm düşünün." : "");
  return `Taban genişliği ${fmt(maxB)} m'ye kadar denendi, kontroller sağlanmadı.${near}${water}${line}${hint}`;
}
/** Bu değerin altındaki içsel sürtünme açısı (kohezyonsuz) ağırlık/konsol duvar için olağan dışı zayıf sayılır */
const WEAK_PHI = 20;

const LABELS: Record<string, string> = { "masonry-gravity": "Taş (kargir) ağırlık duvar", "rc-cantilever": "Betonarme konsol duvar", "rc-counterfort": "Betonarme payandalı duvar" };
const SHAPE_LABELS: Record<MasonryFamily | "rc", string> = {
  lean: "Paralel yüzlü, dolguya yatık duvar (sabit kalınlık K)",
  "back-vertical": "Ön yüzü eğik, arka yüzü düşey duvar",
  stepped: "Gövdesi aşağı doğru genişleyen duvar (üst/alt kalınlık)",
  rc: "Konsol gövde + taban plağı",
};
const r2 = (v: number): number => Math.round(v * 100) / 100;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;
const snap = (v: number, step: number): number => r3(Math.round(v / step) * step);

type Found = { wall: WallInput; result: WallResult };
/** Tip taramasının sonucu: en ucuz aday ve en dar tabanlı aday (ikisi aynı olabilir) */
type Best<T> = { cheapest: T; narrowest: T };
/** En dar taban tercihi: B küçük olan; eşitlikte (±1 mm) ölçüsü (tutar/hacim) küçük olan */
const narrower = (a: { B: number }, am: number, b: { B: number }, bm: number): boolean => a.B < b.B - 1e-3 || (Math.abs(a.B - b.B) <= 1e-3 && am < bm);
/** En dar adayın ayrıca verilmesi için en ucuza göre gereken en az daralma (m) */
const NARROWER_BY = 0.05;
/** Tarama sayacı (tip başına ayrı): denenen/analizi tamamlanan aday, ilk hesap hatası, kontrolleri sağlamayanların en yakını */
type Counter = { n: number; analyzed: number; error?: string; closest?: { label: string; ratio: number; B: number } };

/** Adayın 1 m'lik keşfi; fiyat tablosu boşsa tutar null döner. */
function priceOf(wall: WallInput, result: WallResult, pricing: Pricing): { cost: number | null; currency: string; materials: AutoSizeCandidate["materials"] } {
  const q = computeQuantities({ ...wall, length: 1 }, result);
  const est = costEstimate([q], pricing);
  const take = (key: string): number => q.lines.find((l) => l.key === key && !l.info)?.perMetre ?? 0;
  return {
    cost: est.priced ? est.total : null,
    currency: est.currency,
    materials: { concrete: take("concrete"), masonry: take("masonry"), steel: take("rebarSmall") + take("rebarLarge"), formwork: take("formwork"), excavation: take("excavation") },
  };
}

/** Karşılaştırma ölçüsü: tutar (fiyatlıysa) yoksa gövde hacmi. */
const metricOf = (p: ReturnType<typeof priceOf>): number => p.cost ?? p.materials.masonry + p.materials.concrete;

/**
 * Taş duvarın keşfi yalnız geometriye bağlıdır (kesit alanı, kazı, grobeton) → analiz yapmadan hesaplanır. Budama için:
 * bir genişliğin tutarı o ana kadarki en ucuz adayı aşıyorsa o biçimde daha geniş denemeye gerek yok.
 */
function masonryMetric(wall: WallInput, pricing: Pricing): number {
  if (wall.geometry.mode !== "parametric") return Infinity;
  const s = buildSection(parametricToPolygon(wall.geometry), deg2rad(wall.backfill.slopeAngle));
  const stub = { structureType: "masonry-gravity", geometry: { polygon: s.polygon, area: s.area, B: s.B, toe: s.toe, heel: s.heel, yTop: s.yTop, embedmentDepth: wall.geometry.embedmentDepth, ...(s.baseSlope > 0 ? { baseSlope: s.baseSlope } : {}) } } as unknown as WallResult;
  return metricOf(priceOf(wall, stub, pricing));
}

/** Taş duvarın taban genişliği (analizsiz, eğik tabanda yatay izdüşüm) — en dar aday budaması için */
function masonryB(wall: WallInput): number {
  if (wall.geometry.mode !== "parametric") return Infinity;
  return buildSection(parametricToPolygon(wall.geometry), deg2rad(wall.backfill.slopeAngle)).B;
}

const CASE_TEXT = (id: string): string => (id === "static" ? "statik" : "deprem");

/**
 * Kontrollerin en zorlayanları (oran ≈ 1 → sınırda). Her kontrolde yönetmelik (TBDY/EC7) oranı ile klasik GK oranının
 * (hedef / GK) büyüğü alınır — motor ikisini birden ister (analyze.ts `allOk`); bilgi amaçlı klasik GK (TBDY, classicGoverns false) sayılmaz. Deprem (1−kv)/(1+kv) durumları tek satırda
 * birleşir; derz (iç kesit) kontrolleri de listeye girer.
 */
export function governingOf(result: WallResult, allowableCompression?: number): { label: string; ratio: number }[] {
  const worst = new Map<string, number>();
  const put = (label: string, ratio: number): void => { if (ratio > 0 && Number.isFinite(ratio)) worst.set(label, Math.max(worst.get(label) ?? 0, ratio)); };
  const names = { overturning: "Devrilme", sliding: "Kayma", bearing: "Taşıma gücü" } as const;
  for (const [caseId, cs] of Object.entries(result.cases)) {
    if (!cs) continue;
    const when = CASE_TEXT(caseId);
    for (const [key, name] of Object.entries(names) as [keyof typeof names, string][]) {
      const c = cs[key];
      const classic = c.classic.informative ? 0 : c.classic.target / Math.max(c.classic.factor, 1e-9);
      put(`${name} (${when})`, Math.max(c.tbdy?.ratio ?? 0, classic));
    }
    const e = cs.eccentricity;
    if (e.limit > 0) put(`Dış merkezlilik (${when})`, Math.abs(e.e) / e.limit);
  }
  for (const j of result.joints) {
    for (const [caseId, jc] of Object.entries(j.cases) as [string, JointCaseResult | undefined][]) {
      if (!jc) continue;
      const when = CASE_TEXT(caseId);
      if (jc.eLimit > 0) put(`Derz dış merkezliliği (${when})`, Math.abs(jc.e) / jc.eLimit);
      put(`Derz kayması (${when})`, jc.shearTarget / Math.max(jc.shearFactor, 1e-9));
      if (allowableCompression && allowableCompression > 0) put(`Derz basıncı (${when})`, jc.sigmaMax / allowableCompression);
    }
  }
  return [...worst].map(([label, ratio]) => ({ label, ratio: r2(ratio) })).sort((a, b) => b.ratio - a.ratio).slice(0, 3);
}

/**
 * Taş duvar biçimi: aile + ön yüz eğimi m + temel bloğu + taban eğimi bs (tanα, topuk alçak; KGM Şekil 4.14–4.17). Tarama değişkeni `w` aileye göre:
 * - lean: duvar kalınlığı K (yatay, sabit) — ön ve arka yüz paralel, ikisi de m eğimiyle dolguya yatık (KGM Şekil 4.15, 5:1);
 * - back-vertical: üst kalınlık t — arka yüz düşey, gövde tabanı t + m·H_gövde;
 * - stepped: gövde alt kalınlığı b (üst kalınlık `t` sabit) — önceki tek kademeli biçim.
 */
interface MasonryShape { family: MasonryFamily; m: number; t: number; hf: number; ext: number; bs: number }

/** Parametrik geometride kalınlıklar ön yüzden yatay ölçülür: arka yüz x = m·y + t(y) → t üst = t alt = K ise arka yüz ön yüze paralel. */
function masonryWall(base: WallInput, H: number, shape: MasonryShape, w: number): WallInput {
  const out = structuredClone(base);
  // Başlangıç duvarı betonarmeyse taş adayına betonarme kabulleri taşınmasın (γ 25, δ = β, yerinde dökme taban δb = φ →
  // taş adayı olduğundan dar çıkıyordu): varsayılan taş malzemesi, δ = 2φ/3, δb = 2φ/3 (grobeton/beton üstü seçimi korunur)
  if (base.geometry.mode === "rc-cantilever") {
    out.material = stoneMaterial(DEFAULT_STONE_PRESET);
    out.backfill.wallFrictionAngle = "two-thirds-phi";
    if (base.foundationSoil.baseFriction.mode !== "concrete") out.foundationSoil.baseFriction = { mode: "two-thirds-phi" };
  }
  const Hb = r3(H - shape.hf);
  const [top, bottom] = shape.family === "lean" ? [w, w] : shape.family === "back-vertical" ? [w, r3(w + shape.m * Hb)] : [shape.t, r3(Math.max(w, shape.t))];
  out.geometry = {
    mode: "parametric",
    frontBatter: shape.m,
    steps: [{ height: Hb, topThickness: top, bottomThickness: bottom }],
    ...(shape.hf > 0 ? { foundation: { height: shape.hf, toeExtension: shape.ext, heelExtension: shape.ext } } : {}),
    embedmentDepth: Math.min(base.geometry.embedmentDepth, r2(H - 0.3)),
    ...(shape.bs > 0 ? { baseSlope: shape.bs } : {}),
  };
  return out;
}

/** Kendi ağırlığı tabana otursun: yatık duvarda ağırlık merkezi topuğun gerisine düşerse dolgu yapılmadan devrilir (inşaat evresi). */
/** Kesit parsel sınırı içinde mi (sınır yoksa her zaman) */
const withinLine = (w: WallInput): boolean => (propertyLineExcess(w.geometry, w.propertyLine) ?? 0) <= 1e-6;

function standsAlone(w: WallInput): boolean {
  if (w.geometry.mode !== "parametric") return true;
  const poly = parametricToPolygon(w.geometry);
  const yMin = Math.min(...poly.map((p) => p.y));   // eğik tabanda topuk en alt nokta
  const heel = Math.max(...poly.filter((p) => Math.abs(p.y - yMin) < 1e-9).map((p) => p.x));
  return centroid(poly).x <= heel + 1e-9;
}

/** Biçimin tarama aralığı (w) ve w'ye karşılık taban genişliği. */
function masonryRange(shape: MasonryShape, H: number, tMin: number, maxB: number): { from: number; to: number; baseOf: (w: number) => number } {
  const Hb = H - shape.hf, e2 = 2 * shape.ext;
  if (shape.family === "lean") return { from: tMin, to: r2(maxB - e2), baseOf: (w) => w + e2 };
  if (shape.family === "back-vertical") return { from: tMin, to: r2(maxB - e2 - shape.m * Hb), baseOf: (w) => w + shape.m * Hb + e2 };
  return { from: Math.max(shape.t, r2(0.25 * H)), to: r2(maxB - e2), baseOf: (w) => Math.max(w, shape.t) + e2 };
}

/**
 * Betonarme konsol aday: gövde tt/tb, taban tf, burun oranı; B taban genişliği. Parsel sınırı varsa o yandaki çıkıntı
 * (burun ya da topuk) sınıra uzaklıkla kırpılır, kalan genişlik öbür yana verilir; ön sınırda ve burunsuz kesitte gövdenin ön
 * yüzü düşey olur (sınıra / ön zemine dayanan yüz düz kalsın).
 */
function rcWall(base: WallInput, H: number, shape: RcShape, B: number, step: number): WallInput {
  const w = structuredClone(base);
  const pl = base.propertyLine;
  let toe = Math.max(0, snap(B * shape.toeFrac, step));
  if (pl?.side === "front") toe = Math.min(toe, pl.clearance);
  if (pl?.side === "back") toe = Math.max(toe, r2(B - shape.tb - pl.clearance));
  const heel = r2(Math.max(0, B - toe - shape.tb));
  w.material = { gamma: 25, allowableCompression: 500, jointFriction: 0.6, jointCohesion: 0, description: "Betonarme C25/30 – B420C" };
  // Donatı adayda otomatik (en ekonomik) seçilir: elle seçilmiş çubuklar yeni kesite uymaz, taramayı gereksiz sınırlar
  if (w.rc) w.rc = { ...w.rc, bars: {} };
  // Taş başlangıçtan gelen BA adayı: topuk üstü sürşarj ağırlığı lehte sayılmaz (yeni BA duvar varsayılanı; tutucu)
  if (base.geometry.mode !== "rc-cantilever") w.checks.surchargeAsWeight = false;
  // Topuktan geçen düşey sanal düzlem dolgu içinde → δ = β; topuk yoksa düzlem gövdenin beton arka yüzü → δ = 2φ/3 (TBDY 16.12.2.9 üst sınırı)
  w.backfill.wallFrictionAngle = heel < 1e-9 ? "two-thirds-phi" : "beta";
  // Yerinde dökme beton taban → δb = φ; kullanıcı "grobeton / beton üstünde" (tanδ 0,6, TBDY Tablo 16.3) seçtiyse korunur
  if (base.foundationSoil.baseFriction.mode !== "concrete") w.foundationSoil.baseFriction = { mode: "phi" };
  w.geometry = {
    mode: "rc-cantilever",
    stem: { height: r2(H - shape.tf), topThickness: shape.tt, bottomThickness: shape.tb, taperSide: pl?.side === "front" || toe < 1e-9 ? "back" : "front" },
    base: { thickness: shape.tf, toeLength: r2(toe), heelLength: heel },
    ...(shape.key ? { key: { depth: 0.4, width: 0.4, offsetFromToe: r2(toe) } } : {}),
    embedmentDepth: Math.min(base.geometry.embedmentDepth, r2(H - 0.3)),
  };
  return w;
}

function analyzeSafe(w: WallInput, counter: Counter): WallResult | null {
  try { return analyzeWall(w); } catch (e) { counter.error ??= e instanceof Error ? e.message : String(e); return null; }
}

/**
 * Bir biçim için tüm kontrolleri sağlayan en küçük genişlik: önce kaba adımla (25 cm) ilk sağlayan genişlik,
 * sonra bir kaba adım geriye ince adımla (5 cm) tarama. İkili arama kullanılmaz — betonarmede topuk büyüdükçe kesit
 * tahkiki bozulabildiğinden "sağlama" genişlikte tam monoton değildir. `valid` analizsiz ön elemedir (geometri koşulu).
 */
function minPassing(make: (w: number) => WallInput, from: number, to: number, step: number, coarse: number, counter: Counter, o: { valid?: (w: WallInput) => boolean; probeTop?: boolean } = {}): Found | null {
  if (to < from - 1e-9) return null;
  const tryAt = (x: number, c: Counter = counter): Found | null => {
    const w = make(x);
    if (o.valid && !o.valid(w)) return null;
    counter.n++;
    const r = analyzeSafe(w, c);
    if (r) {
      counter.analyzed++;
      if (!r.ok) {
        const g = governingOf(r)[0];
        if (g && g.ratio > 1 && (!counter.closest || g.ratio < counter.closest.ratio)) counter.closest = { ...g, B: r.geometry.B };
      }
    }
    return r?.ok ? { wall: w, result: r } : null;
  };
  // Önce üst sınır: orada kontroller sağlanmıyorsa biçim elenir (tek analiz) — tarama sayısını birkaç kat azaltır. Hesap
  // hata verirse (ör. çok geniş tabanda arka yüz aşırı yatık → Coulomb kaması yok) karar verilemez, normal taramaya geçilir.
  if (o.probeTop) {
    const probe: Counter = { n: 0, analyzed: 0 };
    if (!tryAt(to, probe) && probe.error === undefined) return null;
  }
  let hit: { x: number; found: Found } | null = null;
  for (let x = from; x <= to + 1e-9; x = r2(x + coarse)) { const f = tryAt(x); if (f) { hit = { x, found: f }; break; } }
  // Kaba adım üst sınırı atladıysa sınırın kendisi de denensin
  if (!hit && to - from > 1e-9 && Math.abs(((to - from) / coarse) - Math.round((to - from) / coarse)) > 1e-6) { const f = tryAt(to); if (f) hit = { x: to, found: f }; }
  if (!hit) return null;
  let best = hit.found;
  for (let x = r2(Math.max(from, hit.x - coarse + step)); x < hit.x - 1e-9; x = r2(x + step)) { const f = tryAt(x); if (f) { best = f; break; } }
  return best;
}

export function autoSizeWall(input: WallInput, options: AutoSizeOptions): AutoSizeReport {
  const t0 = Date.now();
  const H = options.height;
  // Ortak girdiler duvardan; sürşarj ve pasif direnç isteğe bağlı olarak diyalogdan geliyor
  const base = structuredClone(input);
  if (options.surcharge !== undefined) base.loads.surcharge = options.surcharge;
  if (options.passive !== undefined) base.foundationSoil.passive.enabled = options.passive;
  const step = options.step ?? 0.05;
  const types = options.types ?? ["masonry-gravity", "rc-cantilever"];
  const pricing = options.pricing ?? makeDefaultPricing();
  const maxB = r2(H * (options.maxBaseRatio ?? 2));
  const coarse = options.coarseStep ?? 0.25;
  if (!(H > 0.5)) throw new Error("Duvar yüksekliği en az 0,5 m olmalı");
  // Tip başına ayrı sayaç: taşın hesap hatası betonarmenin gerekçesine taşınmasın
  const mc: Counter = { n: 0, analyzed: 0 }, rcc: Counter = { n: 0, analyzed: 0 };
  const candidates: AutoSizeCandidate[] = [];
  const failures: AutoSizeReport["failures"] = [];
  const pl = base.propertyLine;
  const shapes = { masonry: types.includes("masonry-gravity") ? masonryShapes(H, pl) : [], rc: types.includes("rc-cantilever") ? rcShapes(H, pl, base.foundationSoil.passive.enabled) : [] };
  const totalShapes = shapes.masonry.length + shapes.rc.length;
  let doneShapes = 0;
  const tick = (): void => { doneShapes++; options.onProgress?.(doneShapes, totalShapes); };

  if (types.includes("masonry-gravity")) {
    const best = bestMasonry(base, H, shapes.masonry, { pricing, maxB, step, coarse, counter: mc, tick });
    if (best) candidates.push(withNarrowest(best, { structureType: "masonry-gravity", label: LABELS["masonry-gravity"]!, tried: mc.n }));
    else failures.push({ structureType: "masonry-gravity", label: LABELS["masonry-gravity"]!, reason: reasonOf(mc, maxB, base) });
  }

  if (types.includes("rc-cantilever")) {
    const best = bestRc(base, H, shapes.rc, { pricing, maxB, step, coarse, counter: rcc, tick });
    if (best) candidates.push(withNarrowest(best, { shape: SHAPE_LABELS.rc, structureType: "rc-cantilever", label: LABELS["rc-cantilever"]!, tried: rcc.n }));
    else {
      // Sınır yüzünden mi? Sınırsız tarama: en ucuz kesitin topuğu/burnu, sınıra gereken uzaklığı verir (o kesit sınırla da bulunur)
      const free = pl ? (() => {
        const b = structuredClone(base); delete b.propertyLine;
        const c = bestRc(b, H, rcShapes(H, undefined, b.foundationSoil.passive.enabled), { pricing, maxB, step, coarse, counter: { n: 0, analyzed: 0 }, tick: () => {} });
        const w = c?.cheapest;
        return w?.wall.geometry.mode === "rc-cantilever" ? { B: w.B, toe: w.wall.geometry.base.toeLength, heel: w.wall.geometry.base.heelLength, cost: w.cost, currency: w.currency } : undefined;
      })() : undefined;
      failures.push({ structureType: "rc-cantilever", label: LABELS["rc-cantilever"]!, reason: reasonOf(rcc, maxB, base), ...(free ? { withoutLine: free } : {}) });
    }
  }

  candidates.sort((a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity) || a.materials.masonry + a.materials.concrete - (b.materials.masonry + b.materials.concrete));
  return { height: H, candidates, failures, tried: mc.n + rcc.n, ms: Date.now() - t0, priced: candidates.some((c) => c.cost !== null) };
}

type CandidateCore = Omit<AutoSizeCandidate, "structureType" | "label" | "tried" | "narrowest">;

/** Tip adayını kurar; en dar tabanlı aday en ucuzdan en az 5 cm darsa `narrowest` olarak eklenir. */
function withNarrowest(best: Best<Omit<CandidateCore, "shape"> & { shape?: string }>, head: Pick<AutoSizeCandidate, "structureType" | "label" | "tried"> & { shape?: string }): AutoSizeCandidate {
  const make = (c: Omit<CandidateCore, "shape"> & { shape?: string }): AutoSizeCandidate => ({ shape: "", ...c, ...head });
  const cheap = make(best.cheapest);
  return best.narrowest.B <= cheap.B - NARROWER_BY + 1e-6 ? { ...cheap, narrowest: make(best.narrowest) } : cheap;
}

/** Eğim → "5:1" gibi okunuş (düşey/yatay); 0 → düşey */
const batterText = (m: number): string => (m < 1e-9 ? "düşey" : `${fmt1(1 / m)}:1`);
const fmt1 = (v: number): string => String(Math.round(v * 10) / 10).replace(".", ",");

/** Taş aday boyut satırları: taban, kalınlıklar, iki yüzün eğimi (arka yüz düşey / dolguya yatık / dolguya açılan), temel bloğu. */
function masonryDims(wall: WallInput, shape: MasonryShape): AutoSizeDim[] {
  if (wall.geometry.mode !== "parametric") return [];
  const st = wall.geometry.steps[0]!;
  const Hb = st.height;
  const backSlope = (shape.m * Hb + st.topThickness - st.bottomThickness) / Hb;   // + → üst arka köşe tabandan geride (dolguya yatık)
  const backNote = Math.abs(backSlope) < 1e-3 ? "düşey" : backSlope > 0 ? "dolguya yatık" : "dolguya doğru açılan";
  const B = st.bottomThickness + 2 * shape.ext;
  return [
    { label: "Taban genişliği B", value: r2(B) },
    ...(shape.family === "lean"
      ? [{ label: "Duvar kalınlığı K", value: r2(st.topThickness) }]
      : [{ label: "Üst kalınlık", value: r2(st.topThickness) }, { label: "Gövde taban kalınlığı", value: r2(st.bottomThickness) }]),
    { label: "Ön yüz eğimi (yatay/düşey)", value: r3(shape.m), unit: "", note: batterText(shape.m) },
    { label: "Arka yüz eğimi (yatay/düşey)", value: r3(Math.abs(backSlope)), unit: "", note: backNote },
    ...(shape.hf > 0 ? [{ label: "Temel bloğu yüksekliği", value: shape.hf }, { label: "Burun / topuk çıkıntısı", value: shape.ext }] : []),
    ...(shape.bs > 0 ? [{ label: "Taban eğimi (topuk alçak)", value: shape.bs, unit: "", note: `1/${fmt1(1 / shape.bs)} · topuk ${fmt1(Math.round(shape.bs * B * 100))} cm alçak` }] : []),
  ];
}

/** Aday biçim adı: eğik tabanlıysa ek */
const shapeLabel = (shape: MasonryShape): string => SHAPE_LABELS[shape.family] + (shape.bs > 0 ? `, eğik tabanlı (topuk alçak, 1/${fmt1(1 / shape.bs)})` : "");

/** Betonarme konsol: biçimler arasında en ucuz ve en dar tabanlı sağlayan kesit (sınır varsa `base.propertyLine` ile kırpılır). */
function bestRc(base: WallInput, H: number, shapes: RcShape[], o: { pricing: Pricing; maxB: number; step: number; coarse: number; counter: Counter; tick: () => void }): Best<Omit<CandidateCore, "shape">> | null {
  return bestOf(shapes.map((shape) => (limitB: number) => {
    o.tick();
    // Üst sınır yoklaması yalnız bir aday bulunduktan sonra (limitB = 1,15·B_en iyi): çok geniş topukta kesit tahkiki
    // bozulabildiğinden 2·H'de yoklamak sağlayan biçimi yanlışlıkla eleyebilir
    const found = minPassing((B) => rcWall(base, H, shape, B, o.step), Math.max(r2(0.3 * H), shape.tb + 0.3), limitB, o.step, o.coarse, o.counter, { probeTop: limitB < o.maxB });
    if (!found || found.wall.geometry.mode !== "rc-cantilever") return null;
    const g = found.wall.geometry;
    return { ...found, dims: [
      { label: "Taban genişliği B", value: r2(g.base.toeLength + g.stem.bottomThickness + g.base.heelLength) },
      { label: "Gövde üst kalınlığı", value: g.stem.topThickness }, { label: "Gövde alt kalınlığı", value: g.stem.bottomThickness },
      { label: "Taban plağı kalınlığı", value: g.base.thickness }, { label: "Burun", value: g.base.toeLength }, { label: "Topuk", value: g.base.heelLength },
      ...(g.key ? [{ label: "Kayma dişi derinliği", value: g.key.depth }] : []),
    ] };
  }), o.pricing, o.maxB);
}

/**
 * Taş duvar: tüm biçimler arasında en ucuzu ve en dar tabanlısı. Taş duvarın tutarı ve taban genişliği yalnız geometriye
 * bağlı olduğundan budama analizsiz yapılır: her biçimde önce o ana kadarki en ucuz adaydan ucuz **ya da** en dar adaydan dar
 * (eşit) kalan en geniş genişlik bulunur; orada kontroller sağlanmıyorsa biçim elenir (tek analiz), sağlanıyorsa en küçük
 * sağlayan genişlik aranır.
 */
function bestMasonry(base: WallInput, H: number, shapes: MasonryShape[], o: { pricing: Pricing; maxB: number; step: number; coarse: number; counter: Counter; tick: () => void }): Best<CandidateCore> | null {
  const tMin = topsFor(H)[0]!;
  type Cand = CandidateCore & { metric: number };
  let best: Best<Cand> | null = null;
  for (const shape of shapes) {
    o.tick();
    const make = (w: number): WallInput => masonryWall(base, H, shape, w);
    const { from, to } = masonryRange(shape, H, tMin, o.maxB);
    if (to < from - 1e-9) continue;
    let hi = to;
    if (best) {
      let last: number | null = null;
      for (let w = from; w <= to + 1e-9; w = r2(w + o.step)) {
        const mw = make(w);
        if (masonryMetric(mw, o.pricing) >= best.cheapest.metric - 1e-9 && masonryB(mw) > best.narrowest.B + 1e-3) break;
        last = w;
      }
      if (last === null) continue;
      hi = last;
    }
    // hi: en ucuz adaydan ucuz ya da en dar adaydan dar kalan en geniş genişlik; orada bile sağlamıyorsa biçim elenir
    const found = minPassing(make, from, hi, o.step, o.coarse, o.counter, { valid: (w) => standsAlone(w) && withinLine(w), probeTop: true });
    if (!found) continue;
    const p = priceOf(found.wall, found.result, o.pricing);
    const metric = metricOf(p);
    const B = found.result.geometry.B;
    const cheaper: boolean = !best || metric < best.cheapest.metric;
    const narrow: boolean = !best || narrower({ B }, metric, best.narrowest, best.narrowest.metric);
    if (!cheaper && !narrow) continue;
    const cand: Cand = {
      wall: found.wall, result: found.result, shape: shapeLabel(shape), dims: masonryDims(found.wall, shape), B,
      cost: p.cost, currency: p.currency, materials: p.materials, governing: governingOf(found.result, found.wall.material.allowableCompression), metric,
    };
    best = { cheapest: cheaper ? cand : best!.cheapest, narrowest: narrow ? cand : best!.narrowest };
  }
  if (!best) return null;
  const strip = ({ metric: _m, ...rest }: Cand): CandidateCore => rest;
  return { cheapest: strip(best.cheapest), narrowest: strip(best.narrowest) };
}

/**
 * Betonarme biçim adaylarından en ucuzu (fiyat yoksa en az malzeme hacmi) ve en dar tabanlısı. Her biçim, o ana kadarki en
 * ucuz adayın taban genişliğinin %15 üstüne kadar taranır (aynı aile içinde daha geniş taban zaten daha pahalı; donatı tutarı
 * analizden gelir) — en dar aday en ucuzdan geniş olamayacağından bu sınır onu da kaçırmaz, ek tarama gerekmez.
 */
function bestOf(
  makers: ((limitB: number) => { wall: WallInput; result: WallResult; dims: AutoSizeDim[] } | null)[],
  pricing: Pricing,
  maxB: number,
): Best<Omit<CandidateCore, "shape">> | null {
  type Cand = Omit<CandidateCore, "shape"> & { volume: number };
  let best: Best<Cand> | null = null;
  let limitB = maxB;
  const metric = (c: Cand): number => c.cost ?? c.volume;
  for (const make of makers) {
    const found = make(limitB);
    if (!found) continue;
    const { cost, currency, materials } = priceOf(found.wall, found.result, pricing);
    const volume = materials.masonry + materials.concrete;
    const cand: Cand = {
      wall: found.wall, result: found.result, dims: found.dims, B: found.result.geometry.B,
      cost, currency, materials, governing: governingOf(found.result), volume,
    };
    const cheaper: boolean = !best || (cost !== null && best.cheapest.cost !== null ? cost < best.cheapest.cost : volume < best.cheapest.volume);
    const narrow: boolean = !best || narrower(cand, metric(cand), best.narrowest, metric(best.narrowest));
    if (cheaper) limitB = Math.min(maxB, r2(cand.B * 1.15));
    if (cheaper || narrow) best = { cheapest: cheaper ? cand : best!.cheapest, narrowest: narrow ? cand : best!.narrowest };
  }
  if (!best) return null;
  const strip = ({ volume: _v, ...rest }: Cand): Omit<CandidateCore, "shape"> => rest;
  return { cheapest: strip(best.cheapest), narrowest: strip(best.narrowest) };
}

/** Üst kalınlık adayları (m): KGM tip kesitlerinde tepe ≥ 0,50; alçak duvarda 0,40'a kadar */
const topsFor = (H: number): number[] => (H <= 3 ? [0.4, 0.5, 0.6] : H <= 5 ? [0.5, 0.6, 0.7] : [0.6, 0.7, 0.8]);

/**
 * Taş duvar biçim adayları (ucuz olması beklenenler önce — maliyet budaması erken sıkılaşır):
 * 1. Paralel yüzlü yatık duvar: m ∈ {1/5 (KGM 5:1), 1/4, 1/3}, kalınlık K taranır;
 * 2. Ön yüz eğik, arka yüz düşey: m ∈ {1/5, 1/4, 1/3}, üst kalınlık taranır;
 * 3. Kademeli gövde (önceki biçim): üst kalınlık (0,40–0,80), ön eğim (0 · 1/10 · 1/5), gövde alt kalınlığı taranır.
 * Hepsi temel bloğu yok · 0,40 (çıkıntı 0,20) · 0,50 m (çıkıntı 0,30) ile; her biri taban eğimi bs ∈ {0,2 · 0,1 · 0}
 * (topuk alçak, KGM Şekil 4.14–4.17: taban 5:1 ön yüze dik ≈ 1/5) ile. Eğik taban kaymayı belirgin iyileştirdiğinden
 * (depremde çoğu kez hükmeden kontrol) önce denenir: ucuz aday erken bulununca maliyet budaması düz tabanlıları hızla eler.
 */
function masonryShapes(H: number, pl?: PropertyLine): MasonryShape[] {
  // Parsel sınırı: temel bloğu çıkıntısı iki yanda eşit — sınıra uzaklığı aşan bloklar baştan elenir
  const found = [{ hf: 0, ext: 0 }, { hf: 0.4, ext: 0.2 }, { hf: 0.5, ext: 0.3 }].filter((f) => !pl || f.ext <= pl.clearance + 1e-9);
  const out: MasonryShape[] = [];
  for (const bs of BASE_SLOPES) {
    for (const family of ["lean", "back-vertical"] as const) for (const m of [0.2, 0.25, r3(1 / 3)]) for (const f of found) out.push({ family, m, t: 0, bs, ...f });
    for (const t of topsFor(H)) for (const m of [0, 0.1, 0.2]) for (const f of found) out.push({ family: "stepped", t, m, bs, ...f });
  }
  return out;
}

/** Taş duvar taban eğimi adayları (tanα, topuk alçak) */
const BASE_SLOPES = [0.2, 0.1, 0] as const;

/**
 * Betonarme konsol biçim adayları: gövde üst 0,25/0,30; gövde alt ve taban plağı kalınlığı ≈ H/12–H/9 (taban gövdeyle aynı ya da
 * 10 cm kalın); burun oranı B'nin 0 (burunsuz L kesit, ön yüz düşey)/0,20/0,30'u (parsel sınırı varsa o yandaki çıkıntı kırpılır); kayma dişi var/yok (yalnız pasif
 * direnç açıkken).
 */
type RcShape = { tt: number; tb: number; tf: number; toeFrac: number; key: boolean };
function rcShapes(H: number, pl?: PropertyLine, passive = true): RcShape[] {
  const thick = [...new Set([0.3, snap(H / 12, 0.05), snap(H / 10, 0.05), snap(H / 9, 0.05)].map((v) => Math.max(0.3, Math.min(1.2, v))))].sort((a, b) => a - b);
  const out: RcShape[] = [];
  const tt = H <= 4 ? 0.25 : 0.3;
  // Burun oranı 0 = burunsuz L kesit (Cem 2026-10-02: depremde topuk üstü dolgu ağırlığı kaymayı iyileştirir, çoğu kez daha ucuz).
  // Sınır gövde yüzündeyse (uzaklık 0) burun oranı anlamsız: tek çıkıntı var — aynı adayı birkaç kez tarama
  const fracs = pl && pl.clearance < 1e-9 ? [0.2] : [0, 0.2, 0.3];
  // Diş yalnız pasif direnç derinliğini artırır: pasif kapalıyken kaymaya katkısı yok, yalnız maliyet — taranmaz
  const keys = passive ? [false, true] : [false];
  for (const tb of thick) for (const df of [0, 0.1]) for (const toeFrac of fracs) for (const key of keys) {
    if (tb < tt) continue;
    out.push({ tt, tb, tf: r2(tb + df), toeFrac, key });
  }
  return out;
}
